import type { ProductivityTotalsDto } from './productivity.js';
import { z } from 'zod';
import { reason } from './schemas.js';
import type { AttendanceTotals } from './attendance.js';

export const PERIOD_STATUSES = ['open', 'closed'] as const;
export type PeriodStatus = (typeof PERIOD_STATUSES)[number];

export const closePeriodSchema = z.object({ note: z.string().trim().max(500).optional() }).strict();
export const reopenPeriodSchema = z.object({ reason }).strict();

export interface PeriodDto {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  status: PeriodStatus;
  closedAt: string | null;
  closedBy: string | null;
  closeVersion: number;
  /** Today (company time) falls inside this period. */
  isCurrent: boolean;
  hasEnded: boolean;
  hasStarted: boolean;
}

export interface PeriodLeaveLine {
  leaveTypeId: string;
  name: string;
  color: string;
  days: number;
}

/** One employee's figures for one reporting period. Stored as-is when a period is closed. */
export interface PeriodEmployeeSummary {
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  departmentName: string | null;
  countryCode: string;
  timezone: string;
  /** Scheduled working days in the period (after holidays/leave the expected time drops, not this count). */
  workdays: number;
  holidays: number;
  attendance: AttendanceTotals;
  leave: PeriodLeaveLine[];
  /** Computer activity in the period (only for people with a connected computer). */
  productivity?: ProductivityTotalsDto | null;
}

export interface PeriodChecks {
  pendingLeaveRequests: number;
  missingPunchDays: number;
  employeesWithMissingPunches: string[];
  /** People whose last day of the period is not over yet in their own time zone. */
  notFinished: string[];
}

export interface PeriodSummaryDto {
  period: PeriodDto;
  /** "live" = calculated now (open period); "closed" = frozen at close. */
  source: 'live' | 'closed';
  version: number | null;
  rows: PeriodEmployeeSummary[];
  totals: AttendanceTotals;
  checks: PeriodChecks | null;
}
